/** Shared utilities for {@link TutorRow}, {@link TutorCard} and {@link TutorsDataTable}. */
import type { LucideIcon } from 'lucide-react';
import { Mail, Phone } from 'lucide-react';
import { findDepartmentByCode, type DepartmentRef } from '@/utils/department-lookup-utils';
import type { TutorBackendTabView, TutorFilters, TutorRecord } from '@/types/inperson/tutor.types';
import {
  TUTOR_INPERSON_MILESTONES,
  TUTOR_INPERSON_SITUATION_CONFIG,
  FOLDER_SITUATION_CONFIG,
  INPERSON_PRO_SITUATION_LABEL,
  type TutorInPersonTabView,
} from '@/components/tutors/tutor-configs';
import {
  formatDeactivationDate,
  getGenderLabel,
  getInPersonExitReasonLabel,
  getTutorInPersonMilestoneStatus,
} from '@/utils/common-utils';
import { resolveUserContacts } from '@/utils/user-contact-utils';
import type { PrimaryContactKind } from '@/types/user.types';

export {
  availabilityKeysToDays,
  completionBarColor,
  formatDeactivationDate,
  getGenderLabel,
  getInPersonExitReasonLabel,
  getTutorAssignmentStatusColor,
  getTutorAssignmentStatusLabel,
  getTutorCriminalRecordStatusConfig,
  getTutorInPersonMilestoneStatus,
} from '@/utils/common-utils';

/** RBAC actions available on a tutor row/card. Single source of truth. */
export interface TutorPageActions {
  canEditRow?: boolean;
  canDeleteRow?: boolean;
  canPrevalidate?: boolean;
  canValidateAssignment?: boolean;
}

/** Extended actions for {@link TutorsDataTable} (adds {@link DataTable}-level permissions). */
export interface TutorDataTablePageActions extends TutorPageActions {
  canExportCsv?: boolean;
}

/** Establishment wish row from `my_establishments.wishes`. */
export interface TutorWish {
  establishmentName?: string;
  establishmentId?: string;
  rank?: number;
  wishRank?: number;
}

/** Primary + optional secondary contact shown on a tutor card. */
export interface TutorContact {
  primary: { icon: LucideIcon; value: string };
  secondaryValue: string | null;
}

/** Latest assignment actors/refusal from `latestAffectation` on the record. */
export interface TutorLatestAssignment {
  proposedBy?: { userFullName?: string };
  validatedBy?: { userFullName?: string };
  refusedBy?: { userFullName?: string };
  refusalReason?: string;
}

/** 360Learning certificate fields stored on `my_documents` (or legacy section). */
export interface TutorInPersonCertificate {
  certDocumentLink?: string;
  certDocumentName?: string;
  attestationNumber?: string;
  obtentionDate?: string;
}

/** One establishment assignment row (wish + optional affectation status). */
export interface InPersonAssignmentItem {
  id?: string;
  establishmentId: string;
  establishmentName?: string;
  wishRank?: number;
  status?: string;
}

/** Wish used to seed assignment rows (`rank` from the wish list). */
export interface InPersonAssignmentWish {
  establishmentId: string;
  establishmentName?: string;
  rank?: number;
}

/** Per-row accept/refuse decision in the assignment modal. */
export type InPersonAssignmentRowDecision = 'accept' | 'refuse' | undefined;

/** Formats a date value as dd/mm/yyyy French format, or "—". */
export function formatTutorShortDate(value: Date | string | null | undefined): string {
  if (!value) return '—';
  const d = new Date(value as string);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

/** Computes a human-readable seniority string from an engagement start date. */
export function computeTutorSeniority(startDate: Date | string | null | undefined): string {
  if (!startDate) return '—';
  const start = new Date(startDate as string);
  if (Number.isNaN(start.getTime())) return '—';
  const now = new Date();
  const months =
    (now.getFullYear() - start.getFullYear()) * 12 + (now.getMonth() - start.getMonth());
  if (months < 1) return '< 1 mois';
  if (months < 12) return `${months} mois`;
  const years = Math.floor(months / 12);
  const rem = months % 12;
  const yearLabel = `${years} an${years > 1 ? 's' : ''}`;
  if (rem > 0) return `${yearLabel} ${rem} mois`;
  return yearLabel;
}

/** `"Name (code)"` — skips a second code when the name already contains it. */
function appendDepartmentCode(name: string, code: string): string {
  if (name.includes(`(${code})`)) return name;
  return `${name} (${code})`;
}

/** Normalises a department value that may be a string code or a {departmentId, departmentName} object. */
export function formatTutorDepartment(d: unknown, catalog: DepartmentRef[] = []): string {
  if (!d) return '';
  if (typeof d === 'object') {
    const rec = d as { departmentName?: string; departmentId?: string };
    const name = rec.departmentName ?? '';
    const code = rec.departmentId ?? '';
    if (name && code) return appendDepartmentCode(name, code);
    if (code) {
      const resolved = findDepartmentByCode(catalog, String(code));
      return resolved ? appendDepartmentCode(resolved.name, resolved.code) : String(code);
    }
    return name;
  }
  if (typeof d !== 'string' && typeof d !== 'number') return '';
  const code = String(d);
  const resolved = findDepartmentByCode(catalog, code);
  return resolved ? appendDepartmentCode(resolved.name, resolved.code) : code;
}

/** Formatted department labels from the tutor's `my_establishments` section. */
export function getTutorDepartments(record: TutorRecord, catalog: DepartmentRef[] = []): string[] {
  const departments = record.sections?.my_establishments?.departments;
  if (!Array.isArray(departments)) return [];
  return departments.map((d) => formatTutorDepartment(d, catalog)).filter(Boolean);
}

/** Establishment wishes, or `[]` when the section is missing. */
export function getTutorWishes(record: TutorRecord): TutorWish[] {
  const wishes = record.sections?.my_establishments?.wishes;
  if (!Array.isArray(wishes)) return [];
  return wishes as TutorWish[];
}

/** True when the tutor has at least one establishment wish. */
export function hasTutorWishes(record: TutorRecord): boolean {
  return getTutorWishes(record).length > 0;
}

/** Situation chip config + icon; falls back to registration-in-progress. */
export function deriveTutorSituation(record: TutorRecord): {
  situationConfig: (typeof TUTOR_INPERSON_SITUATION_CONFIG)[keyof typeof TUTOR_INPERSON_SITUATION_CONFIG];
  SituationIcon: LucideIcon;
} {
  const situationConfig =
    (record.tutorSituation && TUTOR_INPERSON_SITUATION_CONFIG[record.tutorSituation]) ||
    TUTOR_INPERSON_SITUATION_CONFIG.registration_in_progress;
  return { situationConfig, SituationIcon: situationConfig.icon };
}

/** Phone vs email from `primaryContactKind`; secondary is the other channel. */
export function deriveTutorContact(
  user:
    | {
        email?: string | null;
        phone?: string | null;
        primaryContactKind?: PrimaryContactKind | null;
      }
    | undefined,
): TutorContact {
  const resolved = resolveUserContacts(user?.primaryContactKind, user?.email, user?.phone);
  if (resolved.kind === 'phone') {
    return {
      primary: { icon: Phone, value: resolved.primary?.value ?? '—' },
      secondaryValue: resolved.secondary?.value ?? null,
    };
  }
  return {
    primary: { icon: Mail, value: resolved.primary?.value ?? '—' },
    secondaryValue: resolved.secondary?.value ?? null,
  };
}

/** Assignment CTA stays enabled on the reassign tab even without wishes. */
export function isTutorAssignmentButtonDisabled(
  tabView: TutorInPersonTabView,
  hasWishes: boolean,
): boolean {
  return tabView !== 'to_reassign' && !hasWishes;
}

/** Expanded-row colspan per in-person tutor tab. */
const EXPANDED_COL_SPAN: Record<TutorInPersonTabView, number> = {
  tutors: 9,
  pre_registered: 7,
  to_validate: 5,
  to_reassign: 6,
};

/** Colspan for the expanded assignment row (varies by tab column count). */
export function getTutorExpandedColSpan(tab: TutorInPersonTabView): number {
  return EXPANDED_COL_SPAN[tab];
}

/** True when any tutor chip / date / establishment / search filter is set. */
export function hasTutorActiveFilters(filters: TutorFilters): boolean {
  return (
    !!filters.search?.trim() ||
    filters.statuses.length > 0 ||
    filters.subjects.length > 0 ||
    filters.establishmentId !== undefined ||
    (filters.departmentCodes?.length ?? 0) > 0 ||
    filters.availability.length > 0 ||
    !!filters.engagementStartDateFrom ||
    !!filters.engagementStartDateTo ||
    !!filters.engagementEndDateFrom ||
    !!filters.engagementEndDateTo ||
    !!filters.exitDateFrom ||
    !!filters.exitDateTo ||
    !!filters.exitReason
  );
}

/** Pre-validate modal when the actor can prevalidate but not validate. */
export function resolveTutorAssignmentModalMode(
  pageActions?: TutorPageActions,
): 'pre-validate' | 'validate' {
  if (pageActions?.canPrevalidate && !pageActions?.canValidateAssignment) {
    return 'pre-validate';
  }
  return 'validate';
}

/** Display name passed to the delete confirmation dialog. */
export function getTutorDeleteItemName(record: TutorRecord | undefined): string | undefined {
  if (!record) return undefined;
  return `${record.user?.firstName ?? ''} ${record.user?.lastName ?? ''}`.trim();
}

/** Wishes shaped for the assignment modal (rank fallback = list index). */
export function mapTutorAssignmentWishes(record: TutorRecord) {
  return getTutorWishes(record)
    .filter((w) => !!w.establishmentId)
    .map((w, i) => ({
      establishmentId: w.establishmentId as string,
      establishmentName: w.establishmentName,
      rank: w.rank ?? w.wishRank ?? i + 1,
    }));
}

/** Department ids/codes used to pre-fill the reassignment modal. */
export function getTutorReassignDepartmentIds(record: TutorRecord): string[] {
  const departments = record.sections?.my_establishments?.departments;
  if (!Array.isArray(departments)) return [];
  return departments
    .map((d) => {
      if (!d) return '';
      if (typeof d === 'object') {
        return (d as { departmentId?: string }).departmentId ?? '';
      }
      if (typeof d === 'string' || typeof d === 'number') return String(d);
      return '';
    })
    .filter(Boolean);
}

/** Omit empty arrays from API filter params. */
function undefinedIfEmpty<T>(items: T[]): T[] | undefined {
  // List API treats omitted arrays as "no filter"; empty [] would match nothing.
  return items.length > 0 ? items : undefined;
}

/** Map UI filters to list-API query params (empty arrays become `undefined`). */
export function toTutorApiFilterParams(
  filters: TutorFilters,
  availabilityDays: number[],
  tabView?: TutorBackendTabView,
) {
  return {
    tabView,
    search: filters.search?.trim() || undefined,
    establishmentId: filters.establishmentId,
    departmentCodes: undefinedIfEmpty(filters.departmentCodes ?? []),
    statuses: undefinedIfEmpty(filters.statuses),
    subjectIds: undefinedIfEmpty(filters.subjects),
    availabilityDays: undefinedIfEmpty(availabilityDays),
    engagementStartDateFrom: filters.engagementStartDateFrom,
    engagementStartDateTo: filters.engagementStartDateTo,
    engagementEndDateFrom: filters.engagementEndDateFrom,
    engagementEndDateTo: filters.engagementEndDateTo,
    exitDateFrom: filters.exitDateFrom,
    exitDateTo: filters.exitDateTo,
    exitReason: filters.exitReason,
  };
}

/** `firstName lastName` trimmed, or empty string. */
export function getTutorDisplayName(record: TutorRecord): string {
  return `${record.user?.firstName ?? ''} ${record.user?.lastName ?? ''}`.trim();
}

/** Comma-separated department labels, or an em dash. */
export function formatTutorDepartmentsLabel(departments: string[]): string {
  return departments.length > 0 ? departments.join(', ') : '—';
}

/** Onboarding completion percent from `completion.completedSectionsPercentage`. */
export function getTutorCompletionPct(record: TutorRecord): number {
  return record.completion?.completedSectionsPercentage ?? 0;
}

/** Latest assignment payload attached on the list record (`latestAffectation`). */
export function getTutorLatestAssignment(record: TutorRecord): TutorLatestAssignment | undefined {
  return (record as TutorRecord & { latestAffectation?: TutorLatestAssignment }).latestAffectation;
}

/** Validated vs pending color for a registration milestone chip. */
export function getTutorMilestoneColor(
  milestone: { validatedColor: string; pendingColor: string },
  status: string,
): string {
  if (status === 'validated') return milestone.validatedColor;
  return milestone.pendingColor;
}

/** List row id, or a user id already passed in, for the full-state GET. */
export function resolveTutorExpandedUserId(
  expandedRowId: string | null | undefined,
  rows: readonly TutorRecord[],
): string | undefined {
  if (!expandedRowId) return undefined;
  const foundById = rows.find((row) => row.id === expandedRowId);
  if (foundById) return foundById.userId;
  const foundByUserId = rows.find((row) => row.userId === expandedRowId);
  return foundByUserId?.userId ?? expandedRowId;
}

/** Milestone chips for the expanded row (icon + status color). */
export function getTutorRowMilestones(record: TutorRecord) {
  const sections = (record.sections ?? {}) as Record<string, Record<string, unknown>>;
  return TUTOR_INPERSON_MILESTONES.map((m) => ({
    key: m.key,
    label: m.label,
    icon: m.icon,
    color: getTutorMilestoneColor(m, getTutorInPersonMilestoneStatus(sections, m.key)),
  }));
}

/** Flattened labels/flags used by {@link TutorRow} (one pass over the record). */
export function deriveTutorRowDisplay(record: TutorRecord, catalog: DepartmentRef[] = []) {
  const { situationConfig, SituationIcon } = deriveTutorSituation(record);
  const departments = getTutorDepartments(record, catalog);
  const contact = deriveTutorContact(record.user);
  const assignment = getTutorLatestAssignment(record);
  const completionPct = getTutorCompletionPct(record);

  return {
    displayName: getTutorDisplayName(record),
    userId: record.userId,
    situationConfig,
    SituationIcon,
    departments,
    departmentsLabel: formatTutorDepartmentsLabel(departments),
    hasDepartments: departments.length > 0,
    contact,
    hasEmail: !!record.user?.email,
    hasPhone: !!record.user?.phone,
    emailLabel: record.user?.email || 'N/R',
    phoneLabel: record.user?.phone || 'N/R',
    createdAtLabel: formatTutorShortDate(record.createdAt),
    seniorityLabel: computeTutorSeniority(record.createdAt),
    completionPct,
    completionPctLabel: `${Math.round(completionPct)}%`,
    milestones: getTutorRowMilestones(record),
    proposedByName: assignment?.proposedBy?.userFullName || null,
    validatedByName: assignment?.validatedBy?.userFullName || null,
    refusedByName: assignment?.refusedBy?.userFullName || null,
    refusalReasonLabel: assignment?.refusalReason || '—',
    firstWishName: getTutorFirstWishEstablishmentName(record),
  };
}

/** Display bundle returned by {@link deriveTutorRowDisplay}. */
export type TutorRowDisplay = ReturnType<typeof deriveTutorRowDisplay>;

/** Edit / delete / assignment CTA flags from RBAC + current tab. */
export function deriveTutorRowActionFlags(
  record: TutorRecord,
  tabView: TutorInPersonTabView,
  pageActions?: TutorPageActions,
) {
  const canPreValidate = pageActions?.canPrevalidate ?? false;
  const canValidate = pageActions?.canValidateAssignment ?? false;
  let assignmentButtonPreset: 'row-validate-assignment' | 'row-prevalidate-assignment' | null =
    null;
  if (canPreValidate && canValidate) {
    assignmentButtonPreset = 'row-validate-assignment';
  } else if (canPreValidate) {
    assignmentButtonPreset = 'row-prevalidate-assignment';
  } else if (canValidate) {
    assignmentButtonPreset = 'row-validate-assignment';
  }

  return {
    canEdit: pageActions?.canEditRow ?? true,
    canDelete: pageActions?.canDeleteRow ?? false,
    canPreValidate,
    canValidate,
    isAssignmentButtonDisabled: isTutorAssignmentButtonDisabled(tabView, hasTutorWishes(record)),
    assignmentButtonPreset,
  };
}

/** Contact block is shown on tutors / pre-registered cards. */
export function showsTutorCardContact(tab: TutorInPersonTabView): boolean {
  return tab === 'tutors' || tab === 'pre_registered';
}

/** Establishment block is hidden on the pre-registered tab. */
export function showsTutorCardEstablishment(tab: TutorInPersonTabView): boolean {
  return tab === 'tutors' || tab === 'to_validate' || tab === 'to_reassign';
}

/** First wish establishment name (rank 1), if any. */
export function getTutorFirstWishEstablishmentName(record: TutorRecord): string | undefined {
  return getTutorWishes(record)[0]?.establishmentName;
}

/** Normalize availability filter to a string array (legacy single-string support). */
export function selectedTutorAvailabilityDays(filters: TutorFilters): string[] {
  const raw = filters.availability;
  if (Array.isArray(raw)) return raw;
  if (typeof raw === 'string') return [raw];
  return [];
}

/** Actor display name: `userFullName` or first+last. */
export function formatTutorAssignmentActorName(
  actor?: { firstName?: string; lastName?: string; userFullName?: string } | null,
): string {
  if (!actor) return '';
  if (actor.userFullName) return actor.userFullName;
  return [actor.firstName, actor.lastName].filter(Boolean).join(' ');
}

/** Subject ids from engagement, falling back to `my_info`. */
export function resolveTutorEngagementSubjectIds(
  myEngagement: { subjects?: unknown },
  myInfo: { subjects?: unknown },
): string[] {
  if (Array.isArray(myEngagement.subjects)) return myEngagement.subjects as string[];
  if (Array.isArray(myInfo.subjects)) return myInfo.subjects as string[];
  return [];
}

/** Residence department label from a string code or `{ departmentName }`. */
export function formatTutorResidenceDepartment(department: unknown): string | undefined {
  if (!department) return undefined;
  if (typeof department === 'object') {
    const rec = department as { departmentName?: string; departmentId?: string };
    return rec.departmentName ?? rec.departmentId;
  }
  if (typeof department === 'string' || typeof department === 'number') return String(department);
  return undefined;
}

/** Enterprise name when professional situation is `employee`. */
export function getTutorProSituationCompanyName(rawProSituation: unknown): string | null {
  if (!rawProSituation || typeof rawProSituation !== 'object') return null;
  const ps = rawProSituation as {
    status?: string;
    employee?: {
      company?:
        | Array<{ employerName?: string; companyName?: string; establishmentName?: string }>
        | { employerName?: string; companyName?: string; establishmentName?: string };
    };
  };
  if (ps.status !== 'employee') return null;
  const company = Array.isArray(ps.employee?.company)
    ? ps.employee.company[0]
    : (ps.employee?.company ?? {});
  return company?.employerName ?? company?.companyName ?? company?.establishmentName ?? null;
}

/** School or higher-ed establishment name for `student` and `high_school_student` situations. */
export function getTutorProSituationEstablishmentName(rawProSituation: unknown): string | null {
  if (!rawProSituation || typeof rawProSituation !== 'object') return null;
  const ps = rawProSituation as {
    status?: string;
    student?: {
      higherEducationEstablishment?:
        | Array<{ establishmentName?: string }>
        | { establishmentName?: string };
    };
    high_school_student?: {
      schoolEstablishment?: Array<{ establishmentName?: string }> | { establishmentName?: string };
    };
  };
  if (ps.status === 'student') {
    const estab = Array.isArray(ps.student?.higherEducationEstablishment)
      ? ps.student.higherEducationEstablishment[0]
      : (ps.student?.higherEducationEstablishment ?? {});
    return estab?.establishmentName ?? null;
  }
  if (ps.status === 'high_school_student') {
    const estab = Array.isArray(ps.high_school_student?.schoolEstablishment)
      ? ps.high_school_student.schoolEstablishment[0]
      : (ps.high_school_student?.schoolEstablishment ?? {});
    return estab?.establishmentName ?? null;
  }
  return null;
}

/** Engagement dates: section values win over the flattened user-profile copy. */
export function getTutorEffectiveEngagementDates(
  myEngagement: { desiredStartDate?: unknown; engagementEndDate?: unknown },
  userProfile?: { engagementStartDate?: unknown; engagementEndDate?: unknown },
): { start: string | null; end: string | null } {
  return {
    start:
      (myEngagement.desiredStartDate as string | null | undefined) ??
      (userProfile?.engagementStartDate as string | null | undefined) ??
      null,
    end:
      (myEngagement.engagementEndDate as string | null | undefined) ??
      (userProfile?.engagementEndDate as string | null | undefined) ??
      null,
  };
}

/** UI label for course path (`standard` / `sport`). */
export function getTutorCoursePathLabel(coursePath: unknown): string | undefined {
  if (coursePath == null) return undefined;
  if (coursePath === 'standard') return 'Classique';
  if (coursePath === 'sport') return 'Club de sport';
  if (typeof coursePath === 'string') return coursePath;
  return undefined;
}

/** UI label for tutoring type (`in_person`). */
export function getTutorTutoringTypeLabel(tutoringType: unknown): string | undefined {
  if (tutoringType == null) return undefined;
  if (tutoringType === 'in_person') return 'Présentiel';
  if (typeof tutoringType === 'string') return tutoringType;
  return undefined;
}

/** Normalize program objects/ids into chip `{ id, label }` rows. */
export function mapTutorProgramChips(programs: unknown[]): Array<{ id: string; label: string }> {
  return programs.map((p, i) => {
    if (typeof p === 'string') return { id: p, label: p };
    const rec = p as {
      programId?: string;
      id?: string;
      programName?: string;
      name?: string;
      abbreviation?: string;
    };
    return {
      id: String(rec.programId ?? rec.id ?? i),
      label:
        rec.programName ?? rec.name ?? rec.abbreviation ?? rec.programId ?? `Programme ${i + 1}`,
    };
  });
}

/** Nested `user.id` when present, otherwise `record.userId`. */
export function getTutorUserId(record: TutorRecord): string {
  return record.user?.id ?? record.userId;
}

/** Stable React key for a wish row (`id-rank`). */
export function getTutorWishListKey(wish: TutorWish): string {
  const id = wish.establishmentId ?? wish.establishmentName ?? 'wish';
  const rank = wish.rank ?? wish.wishRank;
  return rank != null ? `${id}-${rank}` : id;
}

/** Establishment ids from wishes, dropping blanks. */
export function getTutorWishEstablishmentIds(wishes: TutorWish[]): string[] {
  return wishes.map((w) => w.establishmentId).filter((id): id is string => !!id);
}

/** Resolve subject ids against the catalog; unknown ids are dropped. */
export function resolveTutorSubjectLabels(
  subjectIds: string[],
  allSubjects: Array<{ id: string; label: string }>,
): Array<{ id: string; label: string }> {
  return subjectIds
    .map((id) => {
      const subject = allSubjects.find((s) => s.id === id);
      return subject ? { id: subject.id, label: subject.label } : null;
    })
    .filter((s): s is { id: string; label: string } => s !== null);
}

/** Status string from a nested professional-situation object or raw string. */
export function getTutorProSituationStatus(raw: unknown): string | undefined {
  if (raw && typeof raw === 'object') return (raw as { status?: string }).status;
  if (typeof raw === 'string') return raw;
  return undefined;
}

/** Exit-reason label; appends the free-text note when reason is `other`. */
export function formatTutorExitReasonDisplay(
  rawExitReason: string | null | undefined,
  exitReasonLabel: string | null,
  exitReasonNote: string | null | undefined,
): string | null {
  if (!exitReasonLabel) return null;
  if ((rawExitReason === 'other' || rawExitReason === 'Autre') && exitReasonNote) {
    return `${exitReasonLabel} — ${exitReasonNote}`;
  }
  return exitReasonLabel;
}

/** True when tutoring type, course path, or at least one program is set. */
export function hasTutorEngagementProgram(myEngagement: {
  tutoringType?: unknown;
  coursePath?: unknown;
  programs?: unknown;
}): boolean {
  return Boolean(
    myEngagement.tutoringType ||
    myEngagement.coursePath ||
    (Array.isArray(myEngagement.programs) && myEngagement.programs.length > 0),
  );
}

/** True when the step's section (or nested key) is a non-empty object. Certification 360 uses the uploaded file. */
export function isTutorRegistrationStepComplete(
  sections: Record<string, unknown>,
  step: { key: string; nested?: string },
): boolean {
  if (step.key === 'certification_360') {
    return hasTutorCertificate(sections);
  }
  const sectionData = step.nested
    ? (sections[step.nested] as Record<string, unknown> | undefined)?.[step.key]
    : sections[step.key];
  return !!sectionData && typeof sectionData === 'object' && Object.keys(sectionData).length > 0;
}

/** Wish row display: name, tutor/recruit ratio, and manager. */
export function getTutorWishEstablishmentDisplay(
  wish: TutorWish,
  estab?: {
    name?: string;
    tutorCount?: number;
    volunteersToRecruit?: number;
    managedBy?: { firstName?: string; lastName?: string };
  },
): { name: string; ratio: string | null; managedByName: string | null; hasEstab: boolean } {
  const name = estab?.name ?? wish.establishmentName ?? wish.establishmentId ?? '';
  const ratio =
    estab != null ? `${estab.tutorCount ?? 0}/${estab.volunteersToRecruit ?? '?'}` : null;
  const managedByName = estab?.managedBy
    ? [estab.managedBy.firstName, estab.managedBy.lastName].filter(Boolean).join(' ')
    : null;
  return { name, ratio, managedByName, hasEstab: estab != null };
}

/** Wishes for the assignment modal, preferring live establishment names. */
export function mapTutorAssignmentWishesWithNames(
  wishes: TutorWish[],
  wishEstabs: Record<string, { name?: string }>,
) {
  return wishes
    .filter((w) => !!w.establishmentId)
    .map((w) => ({
      establishmentId: w.establishmentId as string,
      establishmentName: wishEstabs[w.establishmentId ?? '']?.name ?? w.establishmentName,
      rank: w.rank ?? w.wishRank,
    }));
}

/** Weekly availability from engagement, falling back to `my_info`. */
export function getTutorAvailability(
  myEngagement: { availability?: unknown },
  myInfo: { availability?: unknown },
): { weekly?: unknown[] } | undefined {
  return (myEngagement.availability ?? myInfo.availability) as { weekly?: unknown[] } | undefined;
}

/** Nested establishment name, or the flattened `establishmentName` fallback. */
export function getTutorAssignmentEstablishmentName(aff: {
  establishment?: { name?: string };
  establishmentName?: string;
}): string {
  return aff.establishment?.name || aff.establishmentName || 'Établissement';
}

/** Hours suffix for target/done values (`12 h`). */
export function formatTutorHoursLabel(value: unknown): string | undefined {
  if (typeof value === 'string' || typeof value === 'number') return `${value} h`;
  return undefined;
}

/**
 * 360Learning certificate stored at the top level of `my_documents` (mobile upload, 360Learning
 * import or admin). `certification_360` is the legacy section kept as fallback.
 */
export function getTutorInPersonCertificate(
  sections: Record<string, unknown> | undefined,
): TutorInPersonCertificate {
  const read = (source: unknown, ...keys: string[]): string | undefined => {
    if (!source || typeof source !== 'object') return undefined;
    for (const key of keys) {
      const value = (source as Record<string, unknown>)[key];
      if (typeof value === 'string' && value.trim()) return value;
    }
    return undefined;
  };
  const docs = sections?.my_documents;
  const legacy = sections?.certification_360;
  return {
    certDocumentLink:
      read(docs, 'certDocumentLink', 'documentLink') ??
      read(legacy, 'certDocumentLink', 'documentLink'),
    certDocumentName:
      read(docs, 'certDocumentName', 'documentName') ??
      read(legacy, 'certDocumentName', 'documentName'),
    attestationNumber: read(docs, 'attestationNumber') ?? read(legacy, 'attestationNumber'),
    obtentionDate: read(docs, 'obtentionDate') ?? read(legacy, 'obtentionDate'),
  };
}

/** True when a 360Learning certificate file is stored on `my_documents` or the legacy section. */
export function hasTutorCertificate(
  sections: Record<string, unknown> | undefined,
): boolean {
  const cert = getTutorInPersonCertificate(sections);
  return Boolean(cert.certDocumentLink || cert.certDocumentName);
}

/** Flattened expanded-row content (sections, wishes, pro situation, exit). */
export function deriveTutorContentDisplay(record: TutorRecord, catalog: DepartmentRef[] = []) {
  const sections = (record.sections ?? {}) as Record<string, unknown>;
  const myInfo = (sections.my_information ?? {}) as Record<string, unknown> & {
    professionalSituation?: unknown;
    gender?: string;
    department?: unknown;
    subjects?: unknown;
    availability?: unknown;
    address?: string;
    zipCode?: string;
    city?: string;
    country?: string;
    targetHours?: unknown;
    hoursDone?: unknown;
  };
  const myEngagement = (sections.my_engagement ?? {}) as Record<string, unknown> & {
    subjects?: unknown;
    availability?: unknown;
    tutoringType?: unknown;
    coursePath?: unknown;
    programs?: unknown[];
    desiredStartDate?: unknown;
    engagementEndDate?: unknown;
  };
  const userProfile = (record.user as { userProfiles?: Array<Record<string, unknown>> } | undefined)
    ?.userProfiles?.[0];
  const rawProSituation = myInfo.professionalSituation;
  const proSituationStatus = getTutorProSituationStatus(rawProSituation);
  const rawExitReason = userProfile?.exitReason as string | null | undefined;
  const exitReasonLabel = getInPersonExitReasonLabel(rawExitReason);
  const exitReasonNote = userProfile?.exitReasonNote as string | null | undefined;
  const dates = getTutorEffectiveEngagementDates(myEngagement, userProfile);
  const folderConfig =
    FOLDER_SITUATION_CONFIG[record.folderSituation] ?? FOLDER_SITUATION_CONFIG.incomplete;
  const wishes = getTutorWishes(record);
  const exitDateRaw = userProfile?.exitDate as string | null | undefined;

  return {
    userId: getTutorUserId(record),
    folderConfig,
    sections,
    myInfo,
    myEngagement,
    infoSession: (sections.information_session ?? {}) as Record<string, unknown> & {
      hasParticipated?: boolean;
      participatedAt?: string;
    },
    cert360: getTutorInPersonCertificate(sections),
    contract: (sections.contract ?? {}) as Record<string, unknown> & {
      documentLink?: string;
      documentName?: string;
      isValid?: boolean;
      signedDate?: string;
    },
    myDocuments: (sections.my_documents ?? {}) as Record<string, unknown> & {
      criminalRecord?: {
        recordNumber?: string;
        issueDate?: string;
        issuedBy?: string;
        uploadDate?: string;
        fileName?: string;
        documentName?: string;
        documentLink?: string;
        status?: string;
        validationStatus?: string;
      };
    },
    departments: getTutorDepartments(record, catalog),
    wishes,
    wishEstabIds: getTutorWishEstablishmentIds(wishes),
    subjectIds: resolveTutorEngagementSubjectIds(myEngagement, myInfo),
    availability: getTutorAvailability(myEngagement, myInfo),
    residenceDepartment: formatTutorResidenceDepartment(myInfo.department),
    completionPct: getTutorCompletionPct(record),
    effectiveEngagementStartDate: dates.start,
    effectiveEngagementEndDate: dates.end,
    proSituationStatus,
    proSituationLabel: proSituationStatus
      ? (INPERSON_PRO_SITUATION_LABEL[proSituationStatus] ?? null)
      : null,
    proSituationCompanyName: getTutorProSituationCompanyName(rawProSituation),
    proSituationEstablishmentName: getTutorProSituationEstablishmentName(rawProSituation),
    genderLabel: getGenderLabel(myInfo.gender),
    isDeactivated: record.tutorSituation === 'deactivated',
    rawExitReason,
    exitReasonLabel,
    exitReasonDisplay: formatTutorExitReasonDisplay(rawExitReason, exitReasonLabel, exitReasonNote),
    exitDateRaw,
    exitDateFormatted: exitDateRaw ? formatDeactivationDate(exitDateRaw) : null,
    hasProgramSection: hasTutorEngagementProgram(myEngagement),
    targetHoursLabel: formatTutorHoursLabel(myInfo.targetHours),
    hoursDoneLabel: formatTutorHoursLabel(myInfo.hoursDone),
  };
}

/** Display bundle returned by {@link deriveTutorContentDisplay}. */
export type TutorContentDisplay = ReturnType<typeof deriveTutorContentDisplay>;

/** Paris short date-time formatter for 360Learning. */
const LEARNING360_DATE_TIME_FORMAT = new Intl.DateTimeFormat('fr-FR', {
  dateStyle: 'short',
  timeStyle: 'short',
  timeZone: 'Europe/Paris',
});

/** Format a 360Learning ISO timestamp, or "Indisponible". */
export function formatLearning360DateTime(value: string | null): string {
  if (!value) return 'Indisponible';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Indisponible';
  return LEARNING360_DATE_TIME_FORMAT.format(date);
}

/** Hours when the session is long, otherwise minutes and seconds. */
export function formatLearning360TimeSpent(ms: number | null): string {
  if (ms == null || !Number.isFinite(ms) || ms < 0) return 'Indisponible';
  const totalSeconds = Math.round(ms / 1000);
  if (totalSeconds < 60) return `${totalSeconds} s`;
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  if (minutes < 60) {
    return seconds > 0 ? `${minutes} min ${seconds} s` : `${minutes} min`;
  }
  const hours = Math.floor(minutes / 60);
  const remMinutes = minutes % 60;
  if (hours < 24) {
    return remMinutes > 0 ? `${hours} h ${remMinutes} min` : `${hours} h`;
  }
  const days = Math.floor(hours / 24);
  const remHours = hours % 24;
  return remHours > 0 ? `${days} j ${remHours} h` : `${days} j`;
}

/** Flatten API affectations onto {@link InPersonAssignmentItem}. */
export function mapInPersonAssignmentsFromApi(assignmentsData: unknown): InPersonAssignmentItem[] {
  if (!assignmentsData) return [];
  return (assignmentsData as Array<Record<string, any>>).map((aff) => ({
    id: aff.id,
    establishmentId: aff.establishmentId,
    establishmentName: aff.establishment?.name || aff.establishmentName,
    wishRank: aff.wishRank,
    status: aff.status,
  }));
}

/** Wish order wins; overlay the latest affectation per establishment. */
export function mergeAssignmentWishesWithAffectations(
  wishes: InPersonAssignmentWish[] | undefined,
  assignments: InPersonAssignmentItem[],
  resolvedEstabs: Record<string, { name?: string }>,
): InPersonAssignmentItem[] {
  if (!wishes?.length) return assignments;
  const affMap = new Map([...assignments].reverse().map((a) => [a.establishmentId, a]));
  return wishes
    .filter((w) => !!w.establishmentId)
    .map((w, i) => {
      const aff = affMap.get(w.establishmentId);
      return {
        id: aff?.id,
        establishmentId: w.establishmentId,
        establishmentName:
          aff?.establishmentName ?? resolvedEstabs[w.establishmentId]?.name ?? w.establishmentName,
        wishRank: aff?.wishRank ?? w.rank ?? i + 1,
        status: aff?.status,
      };
    });
}

/** Lock a row once another is accepted, or when status is not actionable. */
export function isAssignmentRowLocked(
  assignment: InPersonAssignmentItem,
  displayItems: InPersonAssignmentItem[],
  rowStates: Record<string, { decision?: InPersonAssignmentRowDecision }>,
  lockedByValidated: boolean,
  isPrevalidate: boolean,
  pendingPrevalidatedIds?: ReadonlySet<string>,
): { isValidated: boolean; isPreValidated: boolean; isLocked: boolean } {
  const isValidated = assignment.status === 'validated';
  const isPreValidated = isPrevalidate && assignment.status === 'awaiting_coordinator_validation';
  const isNotActionableForCoordinator =
    !isPrevalidate &&
    assignment.status !== 'awaiting_coordinator_validation' &&
    !isValidated &&
    !pendingPrevalidatedIds?.has(assignment.establishmentId);
  const lockedByOtherAccepted =
    !isPrevalidate &&
    displayItems.some(
      (item) =>
        item.establishmentId !== assignment.establishmentId &&
        rowStates[item.establishmentId]?.decision === 'accept',
    );
  return {
    isValidated,
    isPreValidated,
    isLocked:
      (!isValidated && !isPreValidated && lockedByValidated) ||
      isNotActionableForCoordinator ||
      lockedByOtherAccepted,
  };
}

/** Lock flags + interactive rows for recruiter / coordinator / combined assignment sections. */
export function deriveAssignmentSectionLocks<T extends { status?: string }>(
  displayItems: T[],
  isPrevalidate: boolean,
): {
  lockedByValidated: boolean;
  interactiveItems: T[];
  preLockedByValidated: boolean;
  preInteractiveItems: T[];
  valLockedByValidated: boolean;
  valInteractiveItems: T[];
} {
  const preLockedByValidated = displayItems.some(
    (item) => item.status === 'awaiting_coordinator_validation' || item.status === 'validated',
  );
  const valLockedByValidated = displayItems.some((item) => item.status === 'validated');
  const lockedByValidated = isPrevalidate ? preLockedByValidated : valLockedByValidated;
  return {
    lockedByValidated,
    interactiveItems: displayItems.filter(
      (item) => item.status === 'validated' || !lockedByValidated,
    ),
    preLockedByValidated,
    preInteractiveItems: displayItems.filter(
      (item) => item.status === 'validated' || !preLockedByValidated,
    ),
    valLockedByValidated,
    valInteractiveItems: displayItems.filter(
      (item) =>
        item.status === 'validated' ||
        item.status === 'awaiting_coordinator_validation' ||
        !valLockedByValidated,
    ),
  };
}
